import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/api";
import { useLang } from "@/i18n";
import Layout from "@/components/Layout";
import { PdfViewerModal } from "@/components/PdfViewerModal";
import { toast } from "sonner";
import { Plus, Trash2, X, Apple, Pencil, Copy, FileText, Sparkles } from "lucide-react";

const EMPTY = { name: "", client_id: "", kcal_target: "", protein_g: "", carbs_g: "", fat_g: "", meals: [], substitutions: [], recipes: [] };
const uid = () => Math.random().toString(36).slice(2, 10);

export default function Nutrition() {
  const { t } = useLang();
  const [plans, setPlans] = useState([]);
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(null);
  const [foods, setFoods] = useState([]);
  const [pdfPlan, setPdfPlan] = useState(null);
  const [diary, setDiary] = useState([]);

  const withMacros = (item) => {
    const food = foods.find((x) => x.name.toLowerCase() === (item.name || "").trim().toLowerCase());
    const g = parseFloat(item.quantity);
    if (!food || !(g > 0)) return { ...item, kcal: null, protein: null, carbs: null, fat: null };
    return {
      ...item,
      kcal: Math.round((food.kcal * g) / 100),
      protein: Math.round(food.protein * g / 10) / 10,
      carbs: Math.round(food.carbs * g / 10) / 10,
      fat: Math.round(food.fat * g / 10) / 10,
    };
  };

  const sumMeal = (m) => (m.foods || []).reduce(
    (a, f) => ({ kcal: a.kcal + (f.kcal || 0), protein: a.protein + (f.protein || 0), carbs: a.carbs + (f.carbs || 0), fat: a.fat + (f.fat || 0) }),
    { kcal: 0, protein: 0, carbs: 0, fat: 0 }
  );

  const nav = [
    { to: "/coach", key: "dashboard", label: t("nav.dashboard") },
    { to: "/coach/clients", key: "clients", label: t("nav.clients") },
    { to: "/coach/exercises", key: "exercises", label: t("nav.exercises") },
    { to: "/coach/programs", key: "programs", label: t("nav.programs") },
    { to: "/coach/nutrition", key: "nutrition", label: t("nav.nutrition"), end: true },
    { to: "/coach/supplements", key: "supplements", label: t("nav.supplements") },
    { to: "/coach/stretching", key: "stretching", label: t("nav.stretching") },
    { to: "/coach/shop", key: "shop", label: t("nav.shop") },
    { to: "/coach/testimonials", key: "testimonials", label: t("nav.testimonials") },
    { to: "/coach/education", key: "education", label: t("nav.education") },
  ];

  const load = () => {
    api.get("/nutrition-plans").then((r) => setPlans(r.data));
    api.get("/coach/clients").then((r) => setClients(r.data));
    api.get("/foods").then((r) => setFoods(r.data));
    api.get("/coach/food-diary").then((r) => setDiary(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (editId && plans.length && !editing) {
      const p = plans.find((x) => x._id === editId);
      if (p) startEdit(p);
    }
  }, [searchParams, plans]);

  const startNew = () => setEditing({ ...EMPTY });

  const [aiOpen, setAiOpen] = useState(false);
  const [aiClient, setAiClient] = useState("");
  const [aiStyle, setAiStyle] = useState("Paleo modificata ipocalorica");
  const [aiNotes, setAiNotes] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const generateAi = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/coach/nutrition-plans/generate", {
        client_id: aiClient || null, style: aiStyle, notes: aiNotes,
      });
      const p = data.plan;
      setEditing({
        name: p.name || "", client_id: p.client_id || "",
        kcal_target: p.kcal_target ?? "", protein_g: p.protein_g ?? "", carbs_g: p.carbs_g ?? "", fat_g: p.fat_g ?? "",
        meals: (p.meals || []).map((m) => ({
          id: m.id || uid(), name: m.name || "", time: m.time || "", notes: m.notes || "",
          foods: (m.foods || []).map((f) => ({ ...f })),
        })),
        substitutions: (p.substitutions || []).map((s) => ({ ...s })),
        recipes: (p.recipes || []).map((r) => ({ ...r })),
      });
      setAiOpen(false);
      toast.success(t("nu.aiDone"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAiLoading(false);
    }
  };
  const duplicate = async (p) => {
    const { _id, created_at, client_name, ...rest } = p;
    await api.post("/nutrition-plans", { ...rest, client_id: null, name: `${p.name} (copia)` });
    toast.success(t("nu.saved"));
    api.get("/nutrition-plans").then((r) => setPlans(r.data));
  };
  const startEdit = (p) => setEditing({
    _id: p._id, name: p.name, client_id: p.client_id || "",
    kcal_target: p.kcal_target ?? "", protein_g: p.protein_g ?? "", carbs_g: p.carbs_g ?? "", fat_g: p.fat_g ?? "",
    meals: (p.meals || []).map((m) => ({ ...m, foods: (m.foods || []).map((f) => ({ ...f })) })),
    substitutions: (p.substitutions || []).map((s) => ({ ...s })),
    recipes: (p.recipes || []).map((r) => ({ ...r })),
  });

  const setMeal = (i, patch) => setEditing((e) => ({ ...e, meals: e.meals.map((m, j) => (j === i ? { ...m, ...patch } : m)) }));

  const save = async () => {
    const payload = {
      ...editing,
      client_id: editing.client_id || null,
      kcal_target: editing.kcal_target ? Number(editing.kcal_target) : null,
      protein_g: editing.protein_g ? Number(editing.protein_g) : null,
      carbs_g: editing.carbs_g ? Number(editing.carbs_g) : null,
      fat_g: editing.fat_g ? Number(editing.fat_g) : null,
    };
    try {
      if (editing._id) await api.put(`/nutrition-plans/${editing._id}`, payload);
      else await api.post("/nutrition-plans", payload);
      toast.success(t("nu.saved"));
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    await api.delete(`/nutrition-plans/${id}`);
    load();
  };

  return (
    <Layout nav={nav}>
      <div data-testid="nutrition-page">
        <PdfViewerModal url={pdfPlan ? `${process.env.REACT_APP_BACKEND_URL}/api/coach/nutrition-plans/${pdfPlan._id}/pdf` : ""}
          title={pdfPlan?.name || ""} onClose={() => setPdfPlan(null)} />
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight">{t("nu.title")}</h1>
          {!editing && (
            <div className="flex gap-2 flex-wrap">
              <button data-testid="ai-generate-button" onClick={() => setAiOpen(true)} className="kc-btn-secondary !py-2.5 flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> {t("nu.aiGenerate")}
              </button>
              <button data-testid="new-plan-button" onClick={startNew} className="kc-btn-primary !py-2.5 flex items-center gap-2">
                <Plus className="w-4 h-4" /> {t("nu.new")}
              </button>
            </div>
          )}
        </div>

        {editing ? (
          <div className="kc-card p-6 space-y-4" data-testid="plan-builder">
            <datalist id="kc-foods">
              {foods.map((f) => <option key={f._id} value={f.name} />)}
            </datalist>
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold">{editing._id ? t("nu.edit") : t("nu.new")}</h2>
              <button data-testid="plan-builder-close" onClick={() => setEditing(null)} className="kc-btn-ghost !p-1.5"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("nu.name")}</label>
                <input data-testid="plan-name-input" value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="kc-input mt-1" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("nu.client")}</label>
                <select data-testid="plan-client-select" value={editing.client_id}
                  onChange={(e) => setEditing({ ...editing, client_id: e.target.value })} className="kc-input mt-1">
                  <option value="">{t("nu.noClient")}</option>
                  {clients.map((c) => <option key={c._id} value={c._id}>{c.name} ({c.email})</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[["kcal_target", "nu.kcal"], ["protein_g", "nu.protein"], ["carbs_g", "nu.carbs"], ["fat_g", "nu.fat"]].map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t(label)}</label>
                  <input data-testid={`plan-${k}-input`} type="number" min="0" value={editing[k]}
                    onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} className="kc-input mt-1 font-mono" />
                </div>
              ))}
            </div>

            <div className="space-y-3">
              <p className="kc-eyebrow">{t("nu.meals")}</p>
              {(() => {
                const tot = editing.meals.reduce((a, m) => {
                  const s = sumMeal(m);
                  return { kcal: a.kcal + s.kcal, protein: a.protein + s.protein, carbs: a.carbs + s.carbs, fat: a.fat + s.fat };
                }, { kcal: 0, protein: 0, carbs: 0, fat: 0 });
                return tot.kcal > 0 && (
                  <div className="flex gap-2 flex-wrap font-mono text-xs" data-testid="plan-totals">
                    <span className="kc-badge bg-primary/15 text-primary">{t("nu.planTotal")}: {tot.kcal} kcal</span>
                    <span className="kc-badge bg-secondary text-muted-foreground">P {Math.round(tot.protein)}g{editing.protein_g ? ` / ${editing.protein_g}g` : ""}</span>
                    <span className="kc-badge bg-secondary text-muted-foreground">C {Math.round(tot.carbs)}g{editing.carbs_g ? ` / ${editing.carbs_g}g` : ""}</span>
                    <span className="kc-badge bg-secondary text-muted-foreground">F {Math.round(tot.fat)}g{editing.fat_g ? ` / ${editing.fat_g}g` : ""}</span>
                  </div>
                );
              })()}
              {editing.meals.map((m, i) => (
                <div key={m.id} className="bg-secondary/50 rounded-xl p-4 space-y-2" data-testid={`meal-card-${i}`}>
                  <div className="flex gap-2 flex-wrap items-center">
                    <input data-testid={`meal-name-${i}`} value={m.name} placeholder={t("nu.mealName")}
                      onChange={(e) => setMeal(i, { name: e.target.value })} className="kc-input flex-1 min-w-[140px]" />
                    <input data-testid={`meal-time-${i}`} value={m.time || ""} placeholder={t("nu.time")}
                      onChange={(e) => setMeal(i, { time: e.target.value })} className="kc-input !w-24" />
                    <button data-testid={`meal-delete-${i}`} onClick={() => setEditing({ ...editing, meals: editing.meals.filter((_, j) => j !== i) })}
                      className="kc-btn-ghost !p-2 text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  {(m.foods || []).map((f, fi) => (
                    <div key={fi} className="flex gap-2 items-center">
                      <input data-testid={`food-name-${i}-${fi}`} list="kc-foods" value={f.name} placeholder={t("nu.foodName")}
                        onChange={(e) => setMeal(i, { foods: m.foods.map((x, j) => (j === fi ? withMacros({ ...x, name: e.target.value }) : x)) })}
                        className="kc-input flex-1" />
                      <input data-testid={`food-qty-${i}-${fi}`} type="number" min="0" value={f.quantity} placeholder={t("nu.grams")}
                        onChange={(e) => setMeal(i, { foods: m.foods.map((x, j) => (j === fi ? withMacros({ ...x, quantity: e.target.value }) : x)) })}
                        className="kc-input !w-24 font-mono" />
                      {f.kcal != null && (
                        <span className="text-xs font-mono text-muted-foreground whitespace-nowrap w-20 text-right" data-testid={`food-kcal-${i}-${fi}`}>
                          {f.kcal} kcal
                        </span>
                      )}
                      <button data-testid={`food-delete-${i}-${fi}`} onClick={() => setMeal(i, { foods: m.foods.filter((_, j) => j !== fi) })}
                        className="kc-btn-ghost !p-1.5 text-destructive"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                  {(() => {
                    const tot = sumMeal(m);
                    return tot.kcal > 0 && (
                      <p className="text-xs font-mono text-accent" data-testid={`meal-total-${i}`}>
                        {t("nu.mealTotal")}: {tot.kcal} kcal · P {Math.round(tot.protein)}g · C {Math.round(tot.carbs)}g · F {Math.round(tot.fat)}g
                      </p>
                    );
                  })()}
                  <button data-testid={`add-food-${i}`} className="kc-btn-ghost text-sm flex items-center gap-1"
                    onClick={() => setMeal(i, { foods: [...(m.foods || []), { name: "", quantity: "100", kcal: null, protein: null, carbs: null, fat: null }] })}>
                    <Plus className="w-4 h-4" /> {t("nu.addFood")}
                  </button>
                  <input data-testid={`meal-notes-${i}`} value={m.notes || ""} placeholder={t("nu.notes")}
                    onChange={(e) => setMeal(i, { notes: e.target.value })} className="kc-input !py-2 text-sm" />
                </div>
              ))}
              <button data-testid="add-meal-button" className="kc-btn-secondary flex items-center gap-2"
                onClick={() => setEditing({ ...editing, meals: [...editing.meals, { id: uid(), name: "", time: "", foods: [], notes: "" }] })}>
                <Plus className="w-4 h-4" /> {t("nu.addMeal")}
              </button>
            </div>

              <div className="kc-card p-4 space-y-2" data-testid="substitutions-editor">
                <p className="kc-eyebrow">{t("nu.substitutions")}</p>
                <p className="text-xs text-muted-foreground">{t("nu.substHint")}</p>
                {(editing.substitutions || []).map((s, i) => (
                  <div key={i} className="flex gap-2 items-center" data-testid={`subst-edit-row-${i}`}>
                    <input data-testid={`subst-food-${i}`} value={s.food || ""} placeholder={t("nu.substFood")}
                      onChange={(e) => setEditing({ ...editing, substitutions: editing.substitutions.map((x, j) => (j === i ? { ...x, food: e.target.value } : x)) })}
                      className="kc-input !w-2/5" />
                    <input data-testid={`subst-alt-${i}`} value={s.alternatives || ""} placeholder={t("nu.substAlternatives")}
                      onChange={(e) => setEditing({ ...editing, substitutions: editing.substitutions.map((x, j) => (j === i ? { ...x, alternatives: e.target.value } : x)) })}
                      className="kc-input flex-1" />
                    <button data-testid={`subst-delete-${i}`} onClick={() => setEditing({ ...editing, substitutions: editing.substitutions.filter((_, j) => j !== i) })}
                      className="kc-btn-ghost !p-1.5 text-destructive"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                <button data-testid="subst-add-row" className="kc-btn-ghost text-sm flex items-center gap-1"
                  onClick={() => setEditing({ ...editing, substitutions: [...(editing.substitutions || []), { food: "", alternatives: "" }] })}>
                  <Plus className="w-4 h-4" /> {t("nu.substAdd")}
                </button>
              </div>

              <div className="kc-card p-4 space-y-2" data-testid="recipes-editor">
                <p className="kc-eyebrow">{t("nu.recipes")}</p>
                {(editing.recipes || []).map((r, i) => (
                  <div key={i} className="space-y-1.5 border-b border-border pb-3" data-testid={`recipe-edit-row-${i}`}>
                    <div className="flex gap-2 items-center">
                      <input data-testid={`recipe-name-${i}`} value={r.name || ""} placeholder={t("nu.recipeName")}
                        onChange={(e) => setEditing({ ...editing, recipes: editing.recipes.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })}
                        className="kc-input flex-1" />
                      <button data-testid={`recipe-delete-${i}`} onClick={() => setEditing({ ...editing, recipes: editing.recipes.filter((_, j) => j !== i) })}
                        className="kc-btn-ghost !p-1.5 text-destructive"><X className="w-4 h-4" /></button>
                    </div>
                    <textarea data-testid={`recipe-ingredients-${i}`} value={(r.ingredients || []).join("\n")} placeholder={t("nu.recipeIngredients")}
                      onChange={(e) => setEditing({ ...editing, recipes: editing.recipes.map((x, j) => (j === i ? { ...x, ingredients: e.target.value.split("\n").filter(Boolean) } : x)) })}
                      className="kc-input !py-2 text-sm min-h-[60px]" />
                    <textarea data-testid={`recipe-prep-${i}`} value={r.preparation || ""} placeholder={t("nu.recipePrep")}
                      onChange={(e) => setEditing({ ...editing, recipes: editing.recipes.map((x, j) => (j === i ? { ...x, preparation: e.target.value } : x)) })}
                      className="kc-input !py-2 text-sm min-h-[60px]" />
                  </div>
                ))}
                <button data-testid="recipe-add-row" className="kc-btn-ghost text-sm flex items-center gap-1"
                  onClick={() => setEditing({ ...editing, recipes: [...(editing.recipes || []), { name: "", ingredients: [], preparation: "" }] })}>
                  <Plus className="w-4 h-4" /> {t("nu.recipeAdd")}
                </button>
              </div>

            <button data-testid="save-plan-button" onClick={save} disabled={!editing.name.trim()} className="kc-btn-primary w-full !py-2.5">
              {t("common.save")}
            </button>
          </div>
        ) : plans.length === 0 && diary.length === 0 ? (
          <p className="text-muted-foreground" data-testid="nutrition-empty">{t("nu.empty")}</p>
        ) : (
          <>
          {diary.length > 0 && (
            <div className="kc-card p-5 mb-6" data-testid="diary-section">
              <p className="kc-eyebrow mb-3">{t("nu.diaryCoach")}</p>
              <div className="space-y-2">
                {diary.map((d) => (
                  <div key={d.client_id} className="flex items-center justify-between gap-3 text-sm" data-testid={`diary-row-${d.client_id}`}>
                    <div className="min-w-0">
                      <p className="font-bold truncate">{d.client_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{d.plan_name}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`kc-badge ${d.days_logged >= 5 ? "bg-green-500/15 text-green-600" : d.days_logged >= 3 ? "bg-amber-500/15 text-amber-600" : "bg-destructive/15 text-destructive"}`}>
                        {d.days_logged}/7 {t("nu.diaryDays")}
                      </span>
                      <span className="text-xs text-muted-foreground hidden sm:block">
                        {d.last_log ? `${t("nu.diaryLast")}: ${d.last_log}` : t("nu.diaryNever")}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {plans.map((p) => (
              <div key={p._id} className="kc-card kc-card-hover p-5" data-testid="nutrition-plan-card">
                <div className="flex justify-between items-start gap-2">
                  <Apple className="w-6 h-6 text-accent" />
                  <div className="flex gap-1">
                    <button data-testid={`pdf-plan-${p._id}`} onClick={() => setPdfPlan(p)}
                      title="Anteprima PDF" className="kc-btn-ghost !p-1.5 inline-flex"><FileText className="w-4 h-4" /></button>
                    <button data-testid={`duplicate-plan-${p._id}`} onClick={() => duplicate(p)} title={t("nu.duplicate")} className="kc-btn-ghost !p-1.5"><Copy className="w-4 h-4" /></button>
                    <button data-testid={`edit-plan-${p._id}`} onClick={() => startEdit(p)} className="kc-btn-ghost !p-1.5"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`delete-plan-${p._id}`} onClick={() => remove(p._id)} className="kc-btn-ghost !p-1.5 text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <h3 className="font-bold text-lg mt-2">{p.name}</h3>
                <p className="text-sm text-muted-foreground">{p.client_name || t("nu.noClient")}</p>
                <div className="flex gap-2 mt-3 flex-wrap font-mono text-xs">
                  {p.kcal_target != null && <span className="kc-badge bg-primary/15 text-primary">{p.kcal_target} kcal</span>}
                  {p.protein_g != null && <span className="kc-badge bg-secondary text-muted-foreground">P {p.protein_g}g</span>}
                  {p.carbs_g != null && <span className="kc-badge bg-secondary text-muted-foreground">C {p.carbs_g}g</span>}
                  {p.fat_g != null && <span className="kc-badge bg-secondary text-muted-foreground">F {p.fat_g}g</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-2">{p.meals?.length || 0} {t("nu.meals").toLowerCase()}</p>
              </div>
            ))}
          </div>
          </>
        )}

        {aiOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !aiLoading && setAiOpen(false)}>
            <div className="kc-card p-6 w-full max-w-md space-y-4" data-testid="ai-generate-modal" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">{t("nu.aiTitle")}</h2>
                <button data-testid="ai-modal-close" onClick={() => setAiOpen(false)} disabled={aiLoading} className="kc-btn-ghost !p-1.5"><X className="w-5 h-5" /></button>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("nu.client")}</label>
                <select data-testid="ai-client-select" value={aiClient} onChange={(e) => setAiClient(e.target.value)} className="kc-input mt-1">
                  <option value="">{t("nu.noClient")}</option>
                  {clients.map((c) => <option key={c._id} value={c._id}>{c.name} ({c.email})</option>)}
                </select>
              </div>
              {aiClient && (() => {
                const c = clients.find((x) => x._id === aiClient);
                return c ? (
                  <div className="flex gap-2 flex-wrap font-mono text-xs" data-testid="ai-client-stats">
                    <span className="kc-badge bg-secondary text-muted-foreground">{c.weight_kg ? `${c.weight_kg} kg` : "— kg"}</span>
                    <span className="kc-badge bg-secondary text-muted-foreground">{c.height_cm ? `${c.height_cm} cm` : "— cm"}</span>
                    <span className="kc-badge bg-secondary text-muted-foreground">{c.body_fat_pct ? `BF ${c.body_fat_pct}%` : "BF —"}</span>
                    {(!c.weight_kg || !c.body_fat_pct) && <span className="text-xs text-amber-600 w-full">{t("nu.aiNoData")}</span>}
                  </div>
                ) : null;
              })()}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("nu.aiStyle")}</label>
                <input data-testid="ai-style-input" value={aiStyle} onChange={(e) => setAiStyle(e.target.value)} className="kc-input mt-1" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("nu.aiNotes")}</label>
                <textarea data-testid="ai-notes-input" value={aiNotes} onChange={(e) => setAiNotes(e.target.value)} className="kc-input mt-1 min-h-[70px]" />
              </div>
              <p className="text-xs text-muted-foreground">{t("nu.aiHint")}</p>
              <button data-testid="ai-generate-submit" onClick={generateAi} disabled={aiLoading || !aiStyle.trim()}
                className="kc-btn-primary w-full !py-2.5 flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4" /> {aiLoading ? t("nu.aiGenerating") : t("nu.aiGenerate")}
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
